import type { PortfolioListReportingSummaryResponseDTO } from "web_myplann/Infrastructure/Api/Api";
import { getPortfolioTitle } from "web_myplann/Portfolio/Components/PortfolioSections/portfolioListItemHelpers";
import {
	PORTFOLIO_SORT,
	type PortfolioSortKey,
} from "web_myplann/Portfolio/Components/Redesign/Filter/portfolioSortKey";
import { propertyValue } from "web_myplann/Portfolio/Components/Redesign/ListItem/propertyHelpers";
import { propertyListTitle } from "web_myplann/Portfolio/Components/Redesign/ListItem/propertyListTitle";
import { calculateMonthlyPayment } from "web_myplann/Portfolio/Helpers/calculateMonthlyPayment";
import type { CategoryItem } from "web_myplann/Portfolio/Types/portfolioCategory";
import { PortfolioItemKind } from "web_myplann/Portfolio/Types/portfolioItemKind";
import type { TranslateFunction } from "web_myplann/Shared/TranslateFunction";

const compareText = (a: string, b: string): number =>
	a.localeCompare(b, "cs", { sensitivity: "base" });

const getProducer = (item: CategoryItem): string =>
	item.kind === PortfolioItemKind.Portfolio ? (item.data.producer ?? "") : "";

const getTitle = (item: CategoryItem, t: TranslateFunction): string =>
	item.kind === PortfolioItemKind.Portfolio
		? getPortfolioTitle(item.data, t)
		: propertyListTitle(item.data, t);

const getValue = (
	item: CategoryItem,
	summaryData: PortfolioListReportingSummaryResponseDTO[] | undefined,
): number | null => {
	if (item.kind === PortfolioItemKind.Property) {
		const raw = propertyValue(item.data);
		if (raw === null || raw.trim() === "") {
			return null;
		}
		const parsed = Number(raw);
		return Number.isFinite(parsed) ? parsed : null;
	}
	return (
		summaryData?.find((entry) => entry.id === item.data.id)?.summary
			?.totalValue ?? null
	);
};

const compareNumbersNullsLast =
	(get: (item: CategoryItem) => number | null, desc: boolean) =>
	(a: CategoryItem, b: CategoryItem): number => {
		const valueA = get(a);
		const valueB = get(b);
		if (valueA === null && valueB === null) {
			return 0;
		}
		if (valueA === null) {
			return 1;
		}
		if (valueB === null) {
			return -1;
		}
		return desc ? valueB - valueA : valueA - valueB;
	};

export const sortPortfolioCategoryItems = (
	items: CategoryItem[],
	sort: PortfolioSortKey,
	desc: boolean,
	summaryData: PortfolioListReportingSummaryResponseDTO[] | undefined,
	t: TranslateFunction,
): CategoryItem[] => {
	if (sort === PORTFOLIO_SORT.producer) {
		const sorted = [...items].sort((a, b) =>
			compareText(getProducer(a), getProducer(b)),
		);
		return desc ? sorted.reverse() : sorted;
	}
	if (sort === PORTFOLIO_SORT.value) {
		const byValue = compareNumbersNullsLast(
			(item) => getValue(item, summaryData),
			desc,
		);
		const byMonthlyPayment = compareNumbersNullsLast(
			calculateMonthlyPayment,
			desc,
		);
		return [...items].sort((a, b) => byValue(a, b) || byMonthlyPayment(a, b));
	}

	const sorted = [...items].sort((a, b) =>
		compareText(getTitle(a, t), getTitle(b, t)),
	);
	return desc ? sorted.reverse() : sorted;
};
